"use client"
import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useLocale } from "next-intl"
import { useState } from "react"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { Desktop, LeftArrow, NewText, RightArrow, useIsDesktop } from "ui"
import { getNonUmbracoUrl } from "utils"
import { extractProperty } from "utils/extractProperty"
import ArticleItem from "../ArticleLobby/ArticleItiem"
import { findBlock } from "../ArticleLobby/LobbyArticles"

const StyledContainer = styled.div`
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    position: relative;
    padding-left: 0px;
    padding-right: 0px;
  }
  ${(props) => props.theme.breakpoints.down("sm")} {
    .swiper-slide {
      width: 100%;
    }
  }
`

const StyledContainerInner = styled.div`
  padding-top: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 40px;
    width: 800px;
  }
`

const StyledArrowDiv = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  width: 832px;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 99999999;
  left: 50%;
  button {
    width: 40px;
    height: 40px;
    background: white;
    box-shadow: 0px 4px 8px 0px #00000029;
    display: flex;
    align-items: center;
    &:hover {
      background-color: white;
    }
  }
`

const StyledBulletContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0 4px;
`

const StyledBullet = styled.div<{ active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin: 0 2px;
  cursor: pointer;
  border: ${(props) => (props.active ? "1px solid #0915BC" : "1px solid black")};
  background-color: ${(props) => (props.active ? "#0915BC" : "none")};
`

type Und = string | undefined

type Obj = {
  text: string
  title: string
  logo: string
  date: string
  url: string
  category: string
  articlePicDescktop: string
  articlePicMobile: string
  subTitle: string
  associate: string
  associateText: string
}
type Props = {
  slides: any
  data: any
  websites: any
}
function ArticlesSlider({ slides, data, websites }: Props) {
  const galleryTitle = extractProperty(data, "galleryTitle")
  const array: Obj[] = slides.map(({ node }: any) => {
    const blocks = extractProperty(node, "blocks") as { blockType: string }[]
    const articleHeader = findBlock({ blocks, type: "articleHeader" })
    const articlePic = findBlock({ blocks, type: "articlePic" })
    const category = extractProperty(articleHeader, "articleCategory") as string
    const url = node.url as string
    const content = findBlock({ blocks, type: "contentText" })
    const text = extractProperty(content, "innerText") as Und
    const articlePicDescktop = extractProperty(articlePic, "articlePicDescktop")
    const articlePicMobile = extractProperty(articlePic, "articlePicMobile")
    const title = extractProperty(articleHeader, "articleMainTitle") as Und
    const subTitle = extractProperty(articleHeader, "activitySubTitle") as Und
    const logo = extractProperty(articleHeader, "imageLogo") as Und
    const date = extractProperty(articleHeader, "articleDate") as Und
    const associate = extractProperty(articleHeader, "associate")
    const associateText = websites?.filter((website: any) => website.id === associate[0]?.id)[0]
      ?.namedProperties?.siteName.value
    const obj = {
      text,
      subTitle,
      title,
      logo,
      date,
      url,
      category,
      articlePicDescktop,
      articlePicMobile,
      associate,
      associateText,
    }
    return obj
  })

  const [swiperRef, setSwiperRef] = useState<any>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const isDesktop = useIsDesktop()
  const locale = useLocale()

  return (
    <StyledContainer>
      <StyledContainerInner>
        <NewText
          desktopFontSize={32}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={24}
          mobileFontWeight="400"
          mobileLineHeight={"34.56px"}
          component="h1"
          color={"black"}
        >
          {galleryTitle}
        </NewText>
        <Swiper
          style={{
            paddingRight: isDesktop ? "0px" : "0px",
            paddingTop: "24px",
            paddingBottom: "24px",
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
          }}
          slidesPerView={"auto"}
          spaceBetween={24}
          freeMode={true}
          onSwiper={setSwiperRef}
        >
          {array?.map((i, key: number) => (
            <>
              <SwiperSlide key={key.toString()} style={{ maxWidth: isDesktop ? "325px" : "none" }}>
                <ArticleItem
                  imageMobile={i.articlePicMobile}
                  subTitle={i.subTitle}
                  logo={i.logo}
                  title={i.title}
                  date={i.date}
                  imageDesktop={i.articlePicDescktop}
                  content={i.text}
                  link={getNonUmbracoUrl(i.url)}
                  associationText={i.associateText}
                />
              </SwiperSlide>
            </>
          ))}
        </Swiper>
        {array.length >= 3 && (
          <Desktop>
            <StyledArrowDiv>
              <IconButton
                onClick={() => {
                  swiperRef.slidePrev()
                }}
              >
                {locale === "he" ? <RightArrow /> : <LeftArrow />}
              </IconButton>
              <IconButton
                onClick={() => {
                  swiperRef.slideNext()
                }}
              >
                {locale === "he" ? <LeftArrow /> : <RightArrow />}
              </IconButton>
            </StyledArrowDiv>
          </Desktop>
        )}
        <StyledBulletContainer>
          {slides.map((_: any, index: number) => (
            <StyledBullet
              key={index.toString()}
              onClick={() => {
                swiperRef.slideTo(index)
              }}
              active={index === activeIndex}
            />
          ))}
        </StyledBulletContainer>
      </StyledContainerInner>
    </StyledContainer>
  )
}
export default ArticlesSlider
